import {
  FetchCacheItemSources,
  FetchCacheItemsSources
} from '../../../../types';
import {
  FetchProductSearchesSort,
  FetchProductSearchesCacheKey,
  FetchProductSearchesGqlQuery,
  FetchProductSearchesFilters,
  FetchProductSearchesLimit
} from '../../productsSearchesTypes';

import {
  CacheInfiniteIndexQueryBaseNodeType,
  CacheInfiniteIndexQueryWithFetchItemOptions,
  CacheInfiniteIndexQueryWithoutFetchItemOptions,
  useCacheInfiniteIndexQuery
} from '../../../common/reactQuery/hooks/useCacheInfiniteIndexQuery';

import {
  INITIAL_PRODUCT_SEARCHES_FILTERS,
  INITIAL_PRODUCT_SEARCHES_LIMIT,
  INITIAL_PRODUCT_SEARCHES_SORT,
  ProductsSearchesPermissions
} from '../../productsSearchesConstants';

type CacheProductSearchesWithPrefetchItemOptions =
  CacheInfiniteIndexQueryWithFetchItemOptions;

type CacheProductSearchesWithoutPrefetchItemOptions =
  CacheInfiniteIndexQueryWithoutFetchItemOptions;

interface CacheProductSearchesDefaultOptions {
  cacheKey: FetchProductSearchesCacheKey;
  query: FetchProductSearchesGqlQuery;
  cacheQuery?: FetchProductSearchesGqlQuery;
  initialFilters?: FetchProductSearchesFilters;
  initialSort?: FetchProductSearchesSort;
  initialLimit?: FetchProductSearchesLimit;
  options?: {
    cacheTime?: number;
    staleTime?: number;
    enabled?: boolean;
    enabledPlaceholder?: boolean;
  };
  source?: FetchCacheItemsSources;
}

type CacheProductSearchesOptions = CacheProductSearchesDefaultOptions &
  (
    | CacheProductSearchesWithoutPrefetchItemOptions
    | CacheProductSearchesWithPrefetchItemOptions
  );

const scope = 'productSearches';

function useCacheProductSearches<
  ProductSearchesItemType extends CacheInfiniteIndexQueryBaseNodeType
>({
  cacheKey,
  fetchCacheItemSource = FetchCacheItemSources.MNESIA,
  fetchItemCacheKey,
  fetchItemQuery,
  fetchCacheItemQuery,
  initialFilters = INITIAL_PRODUCT_SEARCHES_FILTERS,
  initialLimit = INITIAL_PRODUCT_SEARCHES_LIMIT,
  initialSort = INITIAL_PRODUCT_SEARCHES_SORT,
  options = {},
  query,
  cacheQuery,
  source = FetchCacheItemsSources.ES
}: CacheProductSearchesOptions) {
  const {
    data,
    items,
    itemsError,
    itemsTotalCount,
    isFetched,
    isLoading,
    isFetchingNextPage,
    isPlaceholderData,
    currentPage,
    currentLimit,
    currentFilters,
    currentSort,
    hasNextPage,
    updateItemCache,
    loadMoreItems,
    filterItems,
    changeItemsFilters,
    clearItemsFilters,
    clearItemsFiltersPersistInitial,
    sortItems,
    limitItems
  } = useCacheInfiniteIndexQuery<ProductSearchesItemType>({
    action: ProductsSearchesPermissions.READ_PRODUCT_SEARCHES_INDEX_ES_QUERY,
    cacheKey,
    cacheQuery,
    defaultApi: 'cache_api',
    fetchCacheItemQuery,
    fetchCacheItemSource,
    fetchItemCacheKey,
    fetchItemQuery,
    initialFilters,
    initialLimit,
    initialSort,
    options,
    query,
    scope,
    source
  });

  return {
    productSearchesData: data,
    productSearches: items,
    productSearchesError: itemsError,
    productSearchesTotalCount: itemsTotalCount,
    productSearchesFetched: isFetched,
    productSearchesLoading: isLoading,
    productSearchesFetchingNextPage: isFetchingNextPage,
    productSearchesIsPlaceholderData: isPlaceholderData,
    productSearchesFilters: currentFilters,
    productSearchesSort: currentSort as FetchProductSearchesSort,
    productSearchesPage: currentPage,
    productSearchesLimit: currentLimit,
    hasNextProductSearchesPage: hasNextPage,
    updateProductSearchesCache: updateItemCache,
    loadMoreProductSearches: loadMoreItems,
    filterProductSearches: filterItems,
    changeProductSearchesFilters: changeItemsFilters,
    clearProductSearchesFilters: clearItemsFilters,
    clearProductSearchesFiltersPersistInitial: clearItemsFiltersPersistInitial,
    sortProductSearches: sortItems,
    limitProductSearches: limitItems
  };
}

export default useCacheProductSearches;
